import { memo, useCallback } from 'react'

import { Cancel } from '@mui/icons-material'
import { Box, TextField } from '@mui/material'
import { ImageObject, ImageObjectInsert } from '@nativewaves/nw-cloud-sdk/content'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'

import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import {
  useSubmissionProgressStore,
  selectSubmissionProgressSetters,
  SubmissionProgress,
  SubmissionButton,
} from '@shared/components/SubmissionProgress'
import { DialogFormContent } from '@shared/layouts'
import { useImageObjectCreateMutation } from 'hooks/mutations/content'
import { imagesOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Images'

type FormStructure = ImageObjectInsert

type NewDialogProps = {
  repositoryId: string
  onSuccess?: (data: ImageObject) => void
  initialValue?: ImageObject
}

const NewDialogRaw = ({ repositoryId, onSuccess, initialValue }: NewDialogProps) => {
  const { t } = useTranslation(['common', 'domain'])
  const handleClose = useCloseHandler()

  const { environmentId } = imagesOverviewRoute.useParams()

  const { setCurrentStep } = useSubmissionProgressStore(useShallow(selectSubmissionProgressSetters))

  const { mutate: createImage, ...imageCreateProps } = useImageObjectCreateMutation({})

  const { handleSubmit, control } = useForm<FormStructure>({
    defaultValues: {
      environmentId,
      repositoryId,
      ...initialValue,
    },
  })

  const handleFormSubmit = useCallback(
    (data: FormStructure) => {
      setCurrentStep({ value: 10, description: t('domain:Workspace.Image.creatingImage') })

      createImage(
        { imageObjectInsert: data },
        {
          onSuccess: (data) => {
            setTimeout(() => {
              handleClose()

              if (onSuccess) {
                setTimeout(() => {
                  onSuccess(data)
                }, 300)
              }
            }, 700)
          },
          onError: () => {
            setCurrentStep({
              value: 100,
              description: t('somethingWentWrong'),
              icon: <Cancel fontSize="small" color="error" />,
            })
          },
        },
      )
    },
    [setCurrentStep, t, createImage, handleClose, onSuccess],
  )

  return (
    <DialogFormContent
      onSubmit={handleSubmit(handleFormSubmit)}
      title={t('domain:Workspace.Image.addImage')}
      SubmitProgressZone={
        <SubmissionProgress isSuccess={imageCreateProps.isSuccess} isError={imageCreateProps.isError} />
      }
      SaveButton={
        <SubmissionButton
          submitCondition={!imageCreateProps.isSuccess && !imageCreateProps.isError}
          successCondition={imageCreateProps.isSuccess}
          errorCondition={imageCreateProps.isError}
          loading={imageCreateProps.isPending}
          disabled={imageCreateProps.isPending || imageCreateProps.isSuccess}
        />
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2, my: 2 }}>
        <Controller
          control={control}
          name="name"
          rules={{
            required: true,
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={t('name')}
              required
              helperText={fieldState.error?.message}
              error={!!fieldState.error}
            />
          )}
        />
      </Box>
    </DialogFormContent>
  )
}

export const NewDialog = memo(NewDialogRaw)
